import React, { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import SendIcon from "@mui/icons-material/Send";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import ReversePaymentDialog from "../../Parent/Detail/payments/ReversePaymentDialog";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import PlaceIcon from "@mui/icons-material/Place";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs from "dayjs";
import {
  useGetInvoiceDetailQuery,
  useSendInvoiceMutation,
  useDeleteInvoiceMutation,
  useDuplicateInvoiceMutation,
  useMarkInvoiceSentMutation,
  useEmailReceiptMutation,
} from "../../../Api/attoDeskApi";
import useUser from "../../../Core/hook/useUser";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm, confirmPresets } from "../../../Components/Confirm";
import { StudentProfileLink, StaffProfileLink } from "../../../Components/ProfileLinks";
import { Link as RouterLink } from "react-router-dom";
import RecordPaymentDialog from "./RecordPaymentDialog";
import VoidInvoiceDialog from "./VoidInvoiceDialog";
import {
  openInvoicePdf,
  openReceiptPdf,
} from "./printHelpers";

// ── Helpers ─────────────────────────────────────────────────────────
const money = (v: number | string | null | undefined) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d?: string | null) => (d ? dayjs(d).format("MMM D, YYYY") : "—");
const fmtDateTime = (d?: string | null) => (d ? dayjs(d).format("MMM D, YYYY [·] h:mm A") : "—");

/** Uppercase label for the parent header/eyebrow — driven by the real
 *  relationship, never hardcoded. Falls back to CONTACT so we never show
 *  the wrong word. */
const parentEyebrow = (relationship?: string | null): string => {
  const clean = (relationship || "").trim();
  return clean ? clean.toUpperCase() : "CONTACT";
};

/** Card title chooses between "Student & Parent" / "Student & Guardian"
 *  / "Student & Contact" based on the actual relationship value. */
const studentContactCardTitle = (relationship?: string | null): string => {
  const r = (relationship || "").toLowerCase();
  if (r === "parent" || r === "mother" || r === "father") return "Student & Parent";
  if (r.includes("guardian")) return "Student & Guardian";
  return "Student & Contact";
};

/** Clickable parent name → /parents/view/:id. Renders plain text when the
 *  parent id is missing so we never build /parents/view/undefined. */
const ParentProfileLink: React.FC<{ id?: number | string | null; name?: string | null; sx?: any; children?: React.ReactNode }> = ({ id, name, sx, children }) => {
  const label = (children as any) ?? name ?? "";
  const idStr = id === undefined || id === null || id === "" || String(id) === "0" ? "" : String(id);
  if (!idStr) return <Box component="span" sx={sx}>{label}</Box>;
  return (
    <Box
      component={RouterLink as any}
      to={`/parents/view/${encodeURIComponent(idStr)}`}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{
        color: "inherit", textDecoration: "none", fontWeight: 600, cursor: "pointer",
        "&:hover": { color: "primary.main", textDecoration: "underline", textUnderlineOffset: 2 },
        ...(sx || {}),
      }}
    >
      {label}
    </Box>
  );
};

/** Activity icon + color palette. Restrained per spec. */
const activityStyle = (action: string): { color: string; bg: string } => {
  const a = (action || "").toLowerCase();
  if (a.includes("payment") || a.includes("receipt_generated")) return { color: "#15803d", bg: "#dcfce7" };
  if (a.includes("void") || a === "invoice_email_failed" || a === "receipt_email_failed") return { color: "#b91c1c", bg: "#fee2e2" };
  if (a.includes("email") || a.includes("sent") || a.includes("resent")) return { color: "#0f766e", bg: "#ccfbf1" };
  if (a.includes("duplicated")) return { color: "#7c3aed", bg: "#ede9fe" };
  if (a.includes("marked_sent") || a.includes("status")) return { color: "#b45309", bg: "#fef3c7" };
  if (a.includes("edited") || a.includes("updated")) return { color: "#1d4ed8", bg: "#dbeafe" };
  return { color: "#1e40af", bg: "#dbeafe" }; // created/default → blue/navy
};

const STATUS_META: Record<string, { label: string; bg: string; fg: string }> = {
  draft:     { label: "Draft",         bg: "#f1f5f9", fg: "#475569" },
  sent:      { label: "Sent",          bg: "#dbeafe", fg: "#1d4ed8" },
  partial:   { label: "Partially Paid", bg: "#fef3c7", fg: "#b45309" },
  paid:      { label: "Paid",          bg: "#dcfce7", fg: "#15803d" },
  overdue:   { label: "Overdue",       bg: "#fee2e2", fg: "#b91c1c" },
  voided:    { label: "Voided",        bg: "#e5e7eb", fg: "#4b5563" },
  cancelled: { label: "Cancelled",     bg: "#e5e7eb", fg: "#4b5563" },
};

// ── Small building blocks ──────────────────────────────────────────
const Card: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 1px 3px rgba(15,23,42,0.03)", ...sx }}>
    {children}
  </Box>
);

const SectionHeader: React.FC<{ title: string; action?: React.ReactNode }> = ({ title, action }) => (
  <Box sx={{ px: 2.5, py: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
    <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.3, textTransform: "uppercase", color: "#0f172a" }}>{title}</Typography>
    {action}
  </Box>
);

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "#94a3b8" }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a", mt: 0.25, wordBreak: "break-word" }}>{value ?? "—"}</Typography>
  </Box>
);

// ── The page ────────────────────────────────────────────────────────
const InvoiceViewPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id: rawId = "" } = useParams<{ id: string }>();
  const invoiceId = Number(rawId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("return") || "/finance/invoices";
  const { canCreate, canEdit, canDelete } = useUser();
  const { showMessage, showErrorMessage } = useNotifier();
  const confirm = useConfirm();

  const canCreateInvoice = canCreate("Invoice");
  const canEditInvoice = canEdit("Invoice");
  const canDeleteInvoice = canDelete("Invoice");
  const canRecordPayment = canCreate("Payment");

  const { data, isLoading, isError, error, refetch } = useGetInvoiceDetailQuery(invoiceId, {
    skip: !Number.isFinite(invoiceId) || invoiceId <= 0,
  });

  const [sendInvoice, { isLoading: sending }] = useSendInvoiceMutation();
  const [deleteInvoice] = useDeleteInvoiceMutation();
  const [duplicateInvoice] = useDuplicateInvoiceMutation();
  const [markInvoiceSent] = useMarkInvoiceSentMutation();
  const [emailReceipt] = useEmailReceiptMutation();

  const [payOpen, setPayOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [emailingId, setEmailingId] = useState<number | null>(null);
  const [reverseTarget, setReverseTarget] = useState<number | null>(null);

  // Bridge printHelpers → notifier so error toasts appear on this page too.
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<{ level: string; message: string }>).detail;
      if (!detail) return;
      if (detail.level === "error") showErrorMessage(detail.message);
      else showMessage(detail.message);
    };
    window.addEventListener("finance:notify", handler);
    return () => window.removeEventListener("finance:notify", handler);
  }, [showErrorMessage, showMessage]);

  const d: any = (data as any)?.data;
  const inv: any = d?.invoice;
  const items: any[] = d?.items || [];
  const payments: any[] = d?.payments || [];
  const parent = d?.parent;
  const location = d?.location;
  const activity: any[] = d?.activity || [];

  const st = String(d?.effective_status || inv?.status || "draft").toLowerCase();
  const meta = STATUS_META[st] || STATUS_META.draft;
  const isVoided = st === "voided" || st === "cancelled";
  const isPaid = st === "paid";
  const isDraft = st === "draft";
  const hasPayments = payments.length > 0;
  const canPay = canRecordPayment && !isVoided && !isPaid && Number(d?.balance_due || 0) > 0;
  const overdueDays = useMemo(() => {
    if (!inv?.dueDate || isVoided || isPaid) return 0;
    return Math.max(0, dayjs().startOf("day").diff(dayjs(inv.dueDate), "day"));
  }, [inv?.dueDate, isVoided, isPaid]);

  const httpStatus = (error as any)?.status;

  // ── Handlers ──
  const doSend = async () => {
    try {
      const r: any = await sendInvoice({ id: inv.id }).unwrap();
      if (r?.status) showMessage(String(inv?.status).toLowerCase() === "sent" ? "Invoice resent." : "Invoice sent to the guardian.");
      else showErrorMessage(r?.message || "Failed to send invoice.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to send invoice.");
    }
  };
  const doMarkSent = async () => {
    try {
      const r: any = await markInvoiceSent(inv.id).unwrap();
      if (r?.status) showMessage("Marked as sent.");
      else showErrorMessage(r?.message || "Failed to mark as sent.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to mark as sent.");
    }
  };
  const doDuplicate = async () => {
    try {
      const r: any = await duplicateInvoice(inv.id).unwrap();
      if (r?.status) {
        showMessage(`Duplicated as ${r?.data?.invoiceNumber}.`);
        if (r?.data?.id) navigate(`/finance/invoices/${r.data.id}`);
      } else {
        showErrorMessage(r?.message || "Duplicate failed.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Duplicate failed.");
    }
  };
  const doDelete = async () => {
    const ok = await confirm(confirmPresets.deletePermanent(`invoice ${inv.invoiceNumber}`));
    if (!ok) return;
    try {
      const r: any = await deleteInvoice(String(inv.id)).unwrap();
      if (r?.status) {
        showMessage("Draft invoice deleted.");
        navigate(returnTo);
      } else showErrorMessage(r?.message || "Failed to delete invoice.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to delete invoice.");
    }
  };
  const doEmailReceipt = async (paymentId: number, receiptNumber: string) => {
    setEmailingId(paymentId);
    try {
      const r: any = await emailReceipt(paymentId).unwrap();
      if (r?.status) showMessage(r?.message || `Receipt ${receiptNumber} emailed.`);
      else showErrorMessage(r?.message || "Failed to email receipt.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to email receipt.");
    } finally {
      setEmailingId(null);
    }
  };

  // ── Error states ──
  if (!Number.isFinite(invoiceId) || invoiceId <= 0) {
    return <ErrorState code={400} message="Invalid invoice link." onBack={() => navigate(returnTo)} />;
  }
  if (isError) {
    if (httpStatus === 403) return <ErrorState code={403} message="You do not have access to this invoice." onBack={() => navigate(returnTo)} />;
    if (httpStatus === 404) return <ErrorState code={404} message="Invoice not found." onBack={() => navigate(returnTo)} />;
    return <ErrorState code={0} message="Unable to load the invoice. Please try again." onRetry={() => refetch()} onBack={() => navigate(returnTo)} />;
  }

  const pageBg = "linear-gradient(180deg, #f4f8ff 0%, #f8fafc 260px, #f8fafc 100%)";

  return (
    <Box sx={{ background: pageBg, minHeight: "100vh", mx: -3, mt: -3, px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      {/* ── Header ── */}
      <Card sx={{ mb: 2, background: `linear-gradient(135deg, ${alpha(meta.fg, 0.06)} 0%, ${alpha(meta.fg, 0.02)} 100%)` }}>
        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          <Button size="small" onClick={() => navigate(returnTo)} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: "none", fontWeight: 700, mb: 1.25, color: "text.secondary" }}>
            Back to Invoices
          </Button>
          {isLoading || !inv ? (
            <>
              <Skeleton width={80} />
              <Skeleton width={280} height={38} />
              <Skeleton width={480} />
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: { md: "flex-start" }, justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "text.disabled" }}>
                  Invoice
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", mt: 0.25 }}>
                  <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, letterSpacing: "-0.02em", color: "#0f172a", lineHeight: 1.15 }}>
                    {inv.invoiceNumber}
                  </Typography>
                  <Chip
                    size="small"
                    label={meta.label}
                    sx={{ height: 24, fontSize: 11, fontWeight: 800, bgcolor: meta.bg, color: meta.fg, border: `1px solid ${alpha(meta.fg, 0.25)}` }}
                  />
                  {location?.location_name && (
                    <Chip
                      size="small"
                      icon={<PlaceIcon sx={{ fontSize: "12px !important" }} />}
                      label={location.location_name}
                      sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: alpha("#0ea5e9", 0.12), color: "#0369a1" }}
                    />
                  )}
                  {overdueDays > 0 && (
                    <Chip
                      size="small"
                      icon={<ErrorOutlineIcon sx={{ fontSize: "12px !important" }} />}
                      label={`${overdueDays} day${overdueDays === 1 ? "" : "s"} overdue`}
                      sx={{ height: 24, fontSize: 11, fontWeight: 800, bgcolor: alpha("#ef4444", 0.12), color: "#b91c1c" }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mt: 1.25 }}>
                  <Field label="Student" value={<StudentProfileLink id={inv.studentId} name={inv.student ? `${inv.student.first_name || ""} ${inv.student.last_name || ""}`.trim() : ""} sx={{ color: "#0f172a" }}>{inv.student ? `${inv.student.first_name || ""} ${inv.student.last_name || ""}`.trim() : "—"}</StudentProfileLink>} />
                  <Field
                    label={parentEyebrow(parent?.relationship)}
                    value={parent?.name ? <ParentProfileLink id={parent.id} name={parent.name}>{parent.name}</ParentProfileLink> : "—"}
                  />
                  <Field label="Issue Date" value={fmtDate(inv.issueDate)} />
                  <Field label="Due Date" value={<span style={{ color: overdueDays > 0 ? "#b91c1c" : "#0f172a" }}>{fmtDate(inv.dueDate)}</span>} />
                </Box>
              </Box>

              {/* Actions */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
                {canPay && (
                  <Button
                    variant="contained"
                    startIcon={<PaidOutlinedIcon />}
                    onClick={() => setPayOpen(true)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", boxShadow: "0 4px 12px rgba(22,163,74,0.28)" }}
                  >
                    Record Payment
                  </Button>
                )}
                {canEditInvoice && !isVoided && (
                  <Button variant="outlined" startIcon={<SendIcon />} onClick={doSend} disabled={sending} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
                    {isDraft ? "Send Invoice" : "Resend"}
                  </Button>
                )}
                <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => openInvoicePdf(inv.id, inv.invoiceNumber)} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
                  Print
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => openInvoicePdf(inv.id, inv.invoiceNumber, "download")} sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
                  PDF
                </Button>
                <Tooltip title="More actions">
                  <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)} sx={{ bgcolor: "#fff", border: "1px solid rgba(15,23,42,0.12)" }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)} slotProps={{ paper: { sx: { minWidth: 220, borderRadius: "12px" } } }}>
                  {canEditInvoice && !isVoided && !isPaid && (
                    <MenuItem onClick={() => { setMoreAnchor(null); navigate(`/invoice/create?id=${inv.id}`); }} dense>
                      <EditOutlinedIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> Edit
                    </MenuItem>
                  )}
                  {canCreateInvoice && (
                    <MenuItem onClick={() => { setMoreAnchor(null); doDuplicate(); }} dense>
                      <ContentCopyIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> Duplicate
                    </MenuItem>
                  )}
                  {canEditInvoice && isDraft && (
                    <MenuItem onClick={() => { setMoreAnchor(null); doMarkSent(); }} dense>
                      <MarkEmailReadOutlinedIcon fontSize="small" sx={{ mr: 1.25, color: "text.secondary" }} /> Mark as Sent
                    </MenuItem>
                  )}
                  {canEditInvoice && !isVoided && (
                    <MenuItem onClick={() => { setMoreAnchor(null); setVoidOpen(true); }} dense sx={{ color: "#b45309" }}>
                      <BlockIcon fontSize="small" sx={{ mr: 1.25 }} /> Void
                    </MenuItem>
                  )}
                  {isDraft && !hasPayments && canDeleteInvoice && (
                    <MenuItem onClick={() => { setMoreAnchor(null); doDelete(); }} dense sx={{ color: "#dc2626" }}>
                      <DeleteOutlineIcon fontSize="small" sx={{ mr: 1.25 }} /> Delete Draft
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>
          )}
        </Box>
      </Card>

      {/* ── Body ── */}
      <Grid container spacing={2}>
        {/* Main column */}
        <Grid item xs={12} md={8}>
          {/* Financial summary — line items. Each class renders as a
              structured row: large subject (biggest text), grade + staff
              (clickable) below, class/mode/location chip, period + fee
              type, right-aligned qty/rate/amount. Manual/legacy lines that
              have no subject_name fall back to the description string in
              place of the subject so they still render safely. */}
          <Card sx={{ mb: 2 }}>
            <SectionHeader title="Line Items" />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3, color: "#475569", bgcolor: "#f8fafc" } }}>
                    <TableCell>Class</TableCell>
                    {/* Fee Type replaces the old Qty column. Numeric quantity
                        for per-class rows still appears as small secondary
                        text below the label ("N classes"), and monthly rows
                        never show "1 class". */}
                    <TableCell>Fee Type</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && [0, 1, 2].map((i) => (
                    <TableRow key={i}>{[0, 1, 2, 3].map((j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                  ))}
                  {!isLoading && items.length === 0 && (
                    <TableRow><TableCell colSpan={4}><Box sx={{ py: 3, textAlign: "center", color: "text.disabled", fontSize: 12.5 }}>No line items.</Box></TableCell></TableRow>
                  )}
                  {!isLoading && items.map((it: any) => {
                    const subject = it.subject_name || it.description || "Line item";
                    const modeLabel = (() => {
                      const m = String(it.teaching_mode || "").toLowerCase();
                      if (m === "online") return "Online";
                      if (m === "offline" || m === "in_person" || m === "inperson") return "In Person";
                      if (m === "hybrid") return "Hybrid";
                      return null;
                    })();
                    const chipParts = [
                      it.class_code || null,
                      it.location_name || null,
                      modeLabel,
                    ].filter(Boolean);
                    // Normalise every backend variation of the billing type
                    // token so `monthly`, `monthly_fee`, `Monthly Fee`,
                    // `per_class`, `per-class`, `Per Class` and legacy
                    // `per_class_session` all resolve to the same label.
                    const btToken = String(it.billing_type || it.itemType || "").toLowerCase().replace(/[\s_-]/g, "");
                    const isPerClass = btToken.includes("perclass");
                    const feeTypeLabel = isPerClass ? "Per Class" : "Monthly Fee";
                    const qtyNumber = Number(it.quantity || 0);
                    const period = it.billing_period_label
                      || it.month_label
                      || (it.period_start ? `${fmtDate(it.period_start)} → ${fmtDate(it.period_end)}` : "");
                    return (
                      <TableRow key={it.id} sx={{ verticalAlign: "top" }}>
                        <TableCell sx={{ py: 1.5 }}>
                          {/* Subject — the largest, boldest identifier */}
                          <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.01em", color: "#0f172a", textTransform: "uppercase", wordBreak: "break-word" }}>
                            {subject}
                          </Typography>
                          {it.grade_name && (
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                              {it.grade_name}
                            </Typography>
                          )}
                          {/* Staff — clickable, right under the subject/grade */}
                          {it.staff_name && (
                            <Typography sx={{ fontSize: 12.5, mt: 0.4 }}>
                              <Box component="span" sx={{ color: "text.secondary" }}>Staff:</Box>{" "}
                              {it.staff_id ? (
                                <StaffProfileLink id={it.staff_id} name={it.staff_name} sx={{ fontSize: 12.5, fontWeight: 700, color: "text.primary" }}>
                                  {it.staff_name}
                                </StaffProfileLink>
                              ) : (
                                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{it.staff_name}</Box>
                              )}
                            </Typography>
                          )}
                          {chipParts.length > 0 && (
                            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.4 }}>
                              {chipParts.join(" • ")}
                            </Typography>
                          )}
                          {(period || feeTypeLabel) && (
                            <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 0.4, fontStyle: "italic" }}>
                              {period ? `${period} ` : ""}{isPerClass ? "Per Class" : "Monthly"} Fee
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          {/* Fee Type replaces the raw quantity. Per-class
                              rows carry a small "N classes" line beneath;
                              monthly rows never show "1 class". */}
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{feeTypeLabel}</Typography>
                          {isPerClass && qtyNumber > 0 && (
                            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
                              {qtyNumber} {qtyNumber === 1 ? "class" : "classes"}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: 12.5 }}>{money(it.rate ?? it.unitPrice)}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{money(it.amount ?? it.totalPrice)}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Payment history */}
          <Card sx={{ mb: 2 }}>
            <SectionHeader
              title="Payment History"
              action={canPay ? (
                <Button size="small" variant="text" onClick={() => setPayOpen(true)} startIcon={<PaidOutlinedIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: "none", fontWeight: 700, color: "#15803d" }}>
                  Record Payment
                </Button>
              ) : null}
            />
            {payments.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <ReceiptLongIcon sx={{ fontSize: 40, color: "#cbd5e1" }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#475569", mt: 1 }}>No payments recorded yet.</Typography>
                <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.5 }}>Payments recorded against this invoice will appear here with printable receipts.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3, color: "#475569", bgcolor: "#f8fafc" } }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Paid By</TableCell>
                      <TableCell>Receipt #</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p: any) => {
                      const busy = emailingId === p.id;
                      // Batch 1D: authoritative payer is payer_parent_id +
                      // payer_name_snapshot. Legacy rows have neither → "Legacy / Unknown".
                      const paidBy = p.payer_name_snapshot
                        || (p.payer_first || p.payer_last ? `${p.payer_first ?? ""} ${p.payer_last ?? ""}`.trim() : null)
                        || (p.payer_parent_id ? `Parent #${p.payer_parent_id}` : "Legacy / Unknown");
                      const isLegacyPayer = !p.payer_parent_id;
                      return (
                        <TableRow key={p.id}>
                          <TableCell><Typography sx={{ fontSize: 12.5 }}>{fmtDate(p.paymentDate)}</Typography></TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: isLegacyPayer ? "#b45309" : "inherit" }}>
                              {paidBy}
                            </Typography>
                          </TableCell>
                          <TableCell><Typography sx={{ fontSize: 12.5, fontWeight: 700, fontFamily: "monospace" }}>{p.receipt_number}</Typography></TableCell>
                          <TableCell><Typography sx={{ fontSize: 12.5 }}>{p.paymentMethod || "—"}</Typography></TableCell>
                          <TableCell><Typography sx={{ fontSize: 12, color: "text.secondary" }}>{p.transactionId || "—"}</Typography></TableCell>
                          <TableCell align="right"><Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#15803d" }}>{money(p.amount)}</Typography></TableCell>
                          <TableCell>
                            <Chip size="small" label={p.status} sx={{ height: 20, fontSize: 10, fontWeight: 700, textTransform: "capitalize", bgcolor: p.status === "completed" ? alpha("#22c55e", 0.12) : alpha("#f59e0b", 0.12), color: p.status === "completed" ? "#15803d" : "#b45309" }} />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                              <Tooltip title="View / Print receipt">
                                <IconButton size="small" onClick={() => openReceiptPdf(p.id, p.receipt_number)}>
                                  <PrintIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download receipt">
                                <IconButton size="small" onClick={() => openReceiptPdf(p.id, p.receipt_number, "download")}>
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Email receipt">
                                <span>
                                  <IconButton size="small" disabled={busy} onClick={() => doEmailReceipt(p.id, p.receipt_number)}>
                                    {busy ? <CircularProgress size={14} /> : <EmailOutlinedIcon sx={{ fontSize: 16 }} />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {/* Batch 1E — Refund / Void (backend chooses operation) */}
                              <Tooltip title="Refund / Void">
                                <IconButton size="small" onClick={() => setReverseTarget(p.id)}>
                                  <UndoOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>

          {/* Activity History moved to the right sidebar; keep the
              main column focused on line items, payment history and
              notes. See ActivityTimeline card in the sidebar below. */}

          {inv?.notes && (
            <Card sx={{ mb: 2 }}>
              <SectionHeader title="Notes" />
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap", color: "text.secondary" }}>{inv.notes}</Typography>
              </Box>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Financial summary */}
          <Card sx={{ mb: 2 }}>
            <SectionHeader title="Financial Summary" />
            {isLoading || !inv ? (
              <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 0.75 }}>
                  <Typography sx={{ fontSize: 13 }}>Subtotal</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>{money(inv.subtotal)}</Typography>
                  {Number(inv.discountAmount) > 0 && (
                    <>
                      <Typography sx={{ fontSize: 13 }}>Discount</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "right", color: "#b45309" }}>−{money(inv.discountAmount)}</Typography>
                    </>
                  )}
                  {Number(inv.taxAmount) > 0 && (
                    <>
                      <Typography sx={{ fontSize: 13 }}>Tax ({inv.taxRate}%)</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>{money(inv.taxAmount)}</Typography>
                    </>
                  )}
                  <Divider sx={{ gridColumn: "1 / span 2", my: 0.75 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Total</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, textAlign: "right" }}>{money(inv.totalAmount)}</Typography>
                  <Typography sx={{ fontSize: 13, color: "#15803d" }}>Paid</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, textAlign: "right", color: "#15803d" }}>{money(d.paid_amount)}</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: Number(d.balance_due) > 0 ? "#b91c1c" : "text.disabled" }}>Balance Due</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, textAlign: "right", color: Number(d.balance_due) > 0 ? "#b91c1c" : "text.disabled" }}>{money(d.balance_due)}</Typography>
                </Box>
                {canPay && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PaidOutlinedIcon />}
                    onClick={() => setPayOpen(true)}
                    sx={{ mt: 2, textTransform: "none", fontWeight: 800, borderRadius: "10px", background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", py: 1, boxShadow: "0 4px 12px rgba(22,163,74,0.28)" }}
                  >
                    Record Payment
                  </Button>
                )}
              </Box>
            )}
          </Card>

          {/* Student + (Parent / Guardian / Contact) — title + labels
              driven by the real relationship value from the backend, not
              hardcoded. */}
          {inv && (
            <Card sx={{ mb: 2 }}>
              <SectionHeader title={studentContactCardTitle(parent?.relationship)} />
              <Box sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Field label="Student" value={<StudentProfileLink id={inv.studentId} name={inv.student ? `${inv.student.first_name || ""} ${inv.student.last_name || ""}`.trim() : ""}>{inv.student ? `${inv.student.first_name || ""} ${inv.student.last_name || ""}`.trim() : "—"}</StudentProfileLink>} />
                  {inv.student?.reference_id && <Field label="Student ID" value={inv.student.reference_id} />}
                  {parent?.name && (
                    <Field
                      label={parentEyebrow(parent?.relationship)}
                      value={<ParentProfileLink id={parent.id} name={parent.name}>{parent.name}</ParentProfileLink>}
                    />
                  )}
                  {parent?.relationship && <Field label="Relationship" value={parent.relationship} />}
                  {parent?.email && <Field label="Email" value={parent.email} />}
                  {parent?.phone && <Field label="Phone" value={parent.phone} />}
                  {location?.location_name && <Field label="Location" value={location.location_name} />}
                </Stack>
              </Box>
            </Card>
          )}

          {/* Activity Timeline — replaces the old Invoice Details card
              (which duplicated header + summary data). Shows the latest
              5 events by default; "View all" expands the rest inline. */}
          {inv && <ActivityTimelineCard activity={activity} />}
        </Grid>
      </Grid>

      {/* ── Dialogs ── */}
      {inv && (
        <>
          <RecordPaymentDialog
            invoice={{ ...inv, balance_due: d?.balance_due, paid_amount: d?.paid_amount, parent_info: parent }}
            open={payOpen}
            onClose={() => setPayOpen(false)}
          />
          <VoidInvoiceDialog invoice={inv} open={voidOpen} onClose={() => setVoidOpen(false)} />
        </>
      )}

      {/* Mobile sticky footer with primary action */}
      {isMobile && canPay && (
        <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, p: 1.25, bgcolor: "#fff", borderTop: "1px solid rgba(15,23,42,0.1)", boxShadow: "0 -4px 12px rgba(15,23,42,0.06)", zIndex: 10 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setPayOpen(true)}
            startIcon={<PaidOutlinedIcon />}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: "10px", background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)" }}
          >
            Record Payment
          </Button>
        </Box>
      )}

      {/* Refund / Void action */}
      {reverseTarget !== null && (
        <ReversePaymentDialog
          open
          paymentId={reverseTarget}
          onClose={() => setReverseTarget(null)}
          onSuccess={() => { setReverseTarget(null); refetch?.(); }}
        />
      )}
    </Box>
  );
};

// ── Activity Timeline card (right sidebar) ─────────────────────────
const ActivityTimelineCard: React.FC<{ activity: any[] }> = ({ activity }) => {
  const [expanded, setExpanded] = useState(false);
  const INITIAL = 5;
  const rows = activity || [];
  const visible = expanded ? rows : rows.slice(0, INITIAL);
  const remaining = Math.max(0, rows.length - INITIAL);

  return (
    <Card sx={{ mb: 2 }}>
      <SectionHeader title="Activity Timeline" />
      <Box sx={{ p: 2 }}>
        {rows.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>No activity recorded for this invoice.</Typography>
        ) : (
          <Box sx={{ position: "relative", pl: 2 }}>
            {/* Vertical timeline line */}
            <Box sx={{ position: "absolute", left: 7, top: 4, bottom: 4, width: 2, bgcolor: "rgba(15,23,42,0.06)" }} />
            <Stack spacing={1.5}>
              {visible.map((a: any, idx: number) => {
                const meta = typeof a.metadata === "string"
                  ? (() => { try { return JSON.parse(a.metadata); } catch { return {}; } })()
                  : (a.metadata || {});
                const title = meta.title || (a.action || "").replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
                const style = activityStyle(a.action || "");
                const actor = meta.actor_name || "System";
                return (
                  <Box key={a.id ?? idx} sx={{ position: "relative" }}>
                    {/* Dot */}
                    <Box
                      sx={{
                        position: "absolute", left: -14, top: 4,
                        width: 12, height: 12, borderRadius: "50%",
                        bgcolor: style.color, boxShadow: `0 0 0 3px ${style.bg}`,
                      }}
                    />
                    <Box sx={{ pl: 0.5 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
                      {a.description && (
                        <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25, wordBreak: "break-word" }}>
                          {a.description}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.35 }}>
                        {actor} · {fmtDateTime(a.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
            {remaining > 0 && (
              <Button
                size="small"
                onClick={() => setExpanded((v) => !v)}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: 11.5, mt: 1.25, ml: -0.5 }}
              >
                {expanded ? "Show fewer" : `View all activity (${rows.length})`}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

// ── Error state ────────────────────────────────────────────────────
const ErrorState: React.FC<{ code: number; message: string; onBack?: () => void; onRetry?: () => void }> = ({ code, message, onBack, onRetry }) => (
  <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
    <Card sx={{ maxWidth: 460, width: "100%", p: 4, textAlign: "center" }}>
      <ErrorOutlineIcon sx={{ fontSize: 48, color: code === 403 ? "#b45309" : "#dc2626" }} />
      <Typography sx={{ fontSize: 18, fontWeight: 800, mt: 1 }}>{code ? `Error ${code}` : "Unable to load invoice"}</Typography>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.75 }}>{message}</Typography>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2.5 }}>
        {onBack && <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: "none", fontWeight: 700 }}>Back to Invoices</Button>}
        {onRetry && <Button variant="contained" onClick={onRetry} sx={{ textTransform: "none", fontWeight: 700 }}>Retry</Button>}
      </Stack>
    </Card>
  </Box>
);

export default InvoiceViewPage;
